import _ from 'lodash';
import tpl from './textfield.tpl.html';
import './textfield.component.scss';

/**
 * Inner validator used by the text field for setting its validness. When setting the validness from an upper scope
 * using element.$setValidity, you should use this validator: `element.$setValidity(TEXTFIELD_VALIDATOR, false)`
 * @type {string}
 */
const TEXTFIELD_VALIDATOR = 'innerValidation';

const TEXTFIELD_INVALID_RAW_VALUE = null;

/**
 * Global ID for text fields, required for the MDC DOM.
 * @type {number}
 */
let id = 0;

class MDCTextFieldComponent {
  constructor($element, $timeout, $scope, $log) {
    'ngInject';

    this.$element = $element;
    this.$timeout = $timeout;
    this.$scope = $scope;
    this.$log = $log;

    /**
     * ID used for the input element.
     * @type {string}
     */
    this.pId = `mdc-text-field-${++id}`;

    /**
     * Name used for the input element.
     * @type {string}
     */
    this.pName = this.pId;

    /**
     * Internal form used for handling validness of input element.
     */
    this.form = null;

    /**
     * Model exposed to outside form.
     */
    this.model = null;

    /**
     * Type of the input element (text, email, tel, etc.).
     *
     * Applies only if field is not multiline.
     *
     * @type {string}
     */
    this.type = 'text';

    /**
     * Is the field disabled?
     * @type {boolean}
     */
    this.isDisabled = false;

    /**
     * Is the field required?
     * @type {boolean}
     */
    this.isRequired = false;

    /**
     * Is the field a multiline field (textarea)?
     * @type {boolean}
     */
    this.isMultiline = false;

    /**
     * Does the field input have a background
     * @type {boolean}
     */
    this.hasBackground = false;

    /**
     * Label for the field displayed on it and moves up dynamically when focused.
     * @type {string}
     */
    this.label = '';

    /**
     * Title for input field. Should be set when label is not set.
     * @type {string}
     */
    this.title = null;

    /**
     * Text shown in case there is an error with the input.
     * @type {string}
     */
    this.validationText = '';

    /**
     * Extra attributes that should be set for the input element.
     * @type {Object.<string, any>}
     */
    this.extraAttrs = {};

    /**
     * Placeholder for the field.
     * @type {string}
     */
    this.placeholder = '';

    /**
     * Max length for the text field, if given. When not given, won't set maxlength on input element.
     * @type {?number}
     */
    this.maxlength = -1;

    /**
     * Custom validator function.
     * @type {Object}
     */
    this.customValidators = {};

    /**
     * Set to true to show a dropdown style triangle at the end of the element.
     * ONLY COMPATIBLE WITH SINGLE LINE FIELDS!
     * @type {boolean}
     */
    this.showDropdown = false;

    /**
     * The autocomplete value to set for the text field. See this documentation for valid values:
     * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
     * @type {string}
     */
    this.pAutocomplete = '';

    /**
     * Should the element be blurred if user presses enter key.
     * @type {boolean}
     */
    this.blurOnEnter = false;

    /**
     * Current focus status
     *
     * @type {boolean}
     */
    this.focused = false;

    /**
     * A callback function that will be called after enter key is pressed.
     */
    this.onEnterCallback = () => {
    };

    /**
     * Custom validation messages object. Object should contain customValidators keyed values
     * of error messages that are shown when the validator fails.
     * @type {?Object}
     */
    this.validationMessages = null;

    // Internal model for input element
    this._text = '';

    // The actual input tag
    this._inputTag = null;

    this._labelElem = null;

    this._helpTextId = this.pId + '-help-text';

    this.icon = null;

    this.debounceDelay = 0;
  }

  $onInit() {
    const div_elem = this.$element.find('div');

    if (this.debounceDelay > 0) {
      this.textChanged = _.debounce(this.textChanged, this.debounceDelay);
    }

    // The input element has not been rendered yet due to the ng-if, so continue when that is done
    this.$timeout(() => {
      window.mdc.MDCTextField.attachTo(div_elem[0]);

      this._inputTag = div_elem[0].querySelector('.mdc-text-field__input');
      this._labelElem = div_elem.find('label');

      // Get the possible pre-existing value from the model, it too is only available after a timeout
      this._text = this.model.$viewValue;

      // Watch the value for changes from the outside
      this.$scope.$watch(() => this.model.$viewValue, () => this._text = this.model.$viewValue);
      this.$scope.$watch(
        () => this._formRawFieldValueWatcher(),
        (newValue, oldValue) => this._formRawFieldValueListener(newValue, oldValue)
      );

      // What the value change for apiValidationText from outside
      this.$scope.$watch(() => this.model.apiValidationText, () => this.apiValidationText = this.model.apiValidationText);

      // Watch errors for changes from the outside, update if upper scope sets an error on us
      this.$scope.$watch(() => this.model.$valid, () => {
        // Validity is true if no errors exist
        if (this.form != null) {
          this.form.$valid = Object.keys(this.model.$error).length === 0;
          this.form.$invalid = !this.form.$valid;
          this.setValidity();
        }
      });

      // Don't make the element taller if there's no label to animate
      if (!this.label) {
        div_elem.removeClass('mdc-text-field--upgraded');
      }

      // Only EDGE of IEs supports -1 maxlength so only add the property when it is set.
      if (this.maxlength !== -1) {
        this._inputTag.setAttribute('maxlength', this.maxlength);
      }

      this._setAttrs(this.extraAttrs, {});

      // Set blurring if set
      if (this.blurOnEnter) {
        this._inputTag.addEventListener('keydown', (event) => {
          if (event.which === 13) {
            this._inputTag.blur();
            event.preventDefault();
            this.onEnterCallback();
          }
        });
      }

      // Check initial validity again after a timeout so that the model value has been propagated to the input field
      this.$timeout(() => this.setValidity(), 50);

      // Further protect this from the form being undefined for some reason, seems to happen mostly on mobiles so maybe
      // a timing thing
      this.$timeout(() => {
        if (this.form != null && this.form[this.pName] != null) {
          Object.assign(this.form[this.pName].$validators, this.customValidators);
        }
      }, 250);
    });
  }

  $onChanges(changes) {
    if ('extraAttrs' in changes) {
      this._setAttrs(changes.extraAttrs.currentValue, changes.extraAttrs.previousValue);
    }
  }

  setValidity() {
    // form can be undefined before entering validity check.
    if (this.form == null) {
      return;
    }

    this.model.$setValidity(TEXTFIELD_VALIDATOR, this.form.$valid);

    // Because MDCTextField validates inputs by HTML5 validation and it is not possible to deactivate
    // this functionality right now, set the custom validity property so that input.checkValidity()
    // returns correctly.
    if (this.form.$invalid) {
      // form.$error is an object with "validator name": "validation info" that have failed.
      const errors = Object.keys(this.form.$error);
      const messages = this.getValidationText(errors);
      if (this._inputTag != null) {
        this._inputTag.setCustomValidity(messages);
      }
    }
    else if (this._inputTag != null) {
      this._inputTag.setCustomValidity('');
    }
  }

  getValidationText(errors) {
    return errors.map(error => {
      // Try to get translated error if it exist but fall back to validation error name
      let txt = error;
      if (this.validationMessages != null && this.validationMessages[error] != null) {
        txt = this.validationMessages[error]
      }

      return txt;
    }).join(', ');
  }

  onFocus() {
    this.focused = true;
  }
  onBlur() {
    this.focused = false;
    this.setTouched();
  }

  setTouched() {
    this.model.$setTouched();
    this.setValidity();
  }

  clear() {
    this._text = '';
    this.textChanged();
  }

  textChanged() {
    this.model.$setViewValue(this._text);
    this.model.apiValidationText = '';
    this.model.$setDirty();
    this.setValidity();
  }

  _setAttrs(new_attrs, old_attrs) {
    if (this._inputTag == null) {
      return;
    }

    for (const attr of Object.keys(new_attrs)) {
      const value = new_attrs[attr];
      if (value != null) {
        this._inputTag.setAttribute(attr, new_attrs[attr]);
      }
      else {
        this._inputTag.removeAttribute(attr);
      }
    }

    for (const attr of Object.keys(old_attrs)) {
      if (!(attr in new_attrs)) {
        this._inputTag.removeAttribute(attr);
      }
    }
  }


  _formGetRawFieldValue() {
    try {
      // When a field is invalid form.$$success.parse is the only place to access the current value of that field
      // The current value is needed for determining if the label of the field should float above it
      if (
        this &&
        this.form &&
        this.form.$$success &&
        this.form.$$success.parse &&
        this.form.$$success.parse.length
      ) {
        return this.form.$$success.parse[0].$viewValue;
      }
    } catch (err) {
      this.$log.debug(err);
    }

    return TEXTFIELD_INVALID_RAW_VALUE;
  }

  _formRawFieldValueWatcher() {
    return this._formGetRawFieldValue();
  }

  _formRawFieldValueListener(newValue, oldValue) {
    if (newValue !== TEXTFIELD_INVALID_RAW_VALUE && newValue !== oldValue) {
      this._text = newValue;
    }
  }
}

const config = {
  templateUrl: tpl,
  controller: MDCTextFieldComponent,
  bindings: {
    pId: '@?',
    pName: '@?',
    type: '@?',
    isDisabled: '<?',
    isRequired: '<?',
    isMultiline: '<?',
    hasBackground: '<?',
    label: '@',
    title: '@?',
    validationText: '@?',
    placeholder: '@',
    maxlength: '<?',
    extraAttrs: '<?',
    customValidators: '<?',
    showDropdown: '<?',
    pAutocomplete: '@?',
    blurOnEnter: '<?',
    onEnterCallback: '&?',
    validationMessages: '<?',
    ngModelOptions: '<?',
    debounceDelay: '<?',
    icon: '@?',
    hasClear: '<?',
    helperText: '@?',
    apiValidationText: '@?'
  },
  require: {
    model: 'ngModel'
  }
};

const mod = angular.module('common.materialComponents.textField', [])

.component('mdcTextField', config);

export {TEXTFIELD_VALIDATOR};
export default mod.name;



// WEBPACK FOOTER //
// ./src/common/material-components/textfields/textfield.component.js